import { Button, Card, Descriptions, Table, Tag, message } from "antd";
import moment from "moment";
import { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { ArrowLeftOutlined } from "@ant-design/icons";
import LoadingBox from "../../../Components/LoadingBox";
import { API } from "../../../Config";
import { GET } from "../../../Config/ApiRequests";
import PageHeader from "../../../Routes/PageHeader";

const PurchaseViewPage = () => {
  const { id } = useParams();
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [purchaseData, setPurchaseData] = useState(null);

  useEffect(() => {
    if (id) {
      fetchPurchaseDetails();
    }
  }, [id]);

  const fetchPurchaseDetails = async () => {
    setLoading(true);
    try {
      const response = await GET(`${API.PURCHASE}/${id}`);
      if (response?.status === true || response?.statusCode === 200) {
        // Extract data from response structure
        setPurchaseData(response?.data || response);
      } else {
        message.error("Failed to load purchase details");
      }
    } catch (error) {
      message.error("Failed to load purchase details");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const getPaymentStatusTag = (status) => {
    if (!status) return <Tag color="default">-</Tag>;
    const tagColor = {
      paid: "success",
      partial: "warning",
      pending: "error",
    };
    return (
      <Tag color={tagColor[status] || "default"}>
        {status?.charAt(0)?.toUpperCase() + status?.slice(1) || "-"}
      </Tag>
    );
  };

  const getPaidStatusTag = (paidAmount, totalAmount) => {
    const totalPaid = Number(paidAmount) || 0;
    const total = Number(totalAmount) || 0;

    if (totalPaid > 0 && totalPaid < total) {
      return <Tag color="orange">Partial</Tag>;
    } else if (totalPaid === total) {
      return <Tag color="green">Paid</Tag>;
    }
    return <Tag color="red">Unpaid</Tag>;
  };

  const detailColumns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: "20%",
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      width: "20%",
      render: (text) => text || "-",
    },
    {
      title: "Price",
      dataIndex: "price",
      key: "price",
      width: "15%",
      render: (text) => Number(text || 0).toFixed(2),
    },
    {
      title: "Quantity",
      dataIndex: "quantity",
      key: "quantity",
      width: "10%",
      render: (text) => Number(text || 0).toFixed(2),
    },
    {
      title: "Discount",
      dataIndex: "discount",
      key: "discount",
      width: "15%",
      render: (text) => Number(text || 0).toFixed(2),
    },
    {
      title: "Total",
      dataIndex: "total",
      key: "total",
      width: "15%",
      render: (text) => Number(text || 0).toFixed(2),
    },
    {
      title: "Paid Amount",
      dataIndex: "paid_amount",
      key: "paid_amount",
      width: "15%",
      render: (text) => Number(text || 0).toFixed(2),
    },
    {
      title: "Paid Status",
      width: "15%",
      render: (_, record) => {
        return getPaidStatusTag(record?.paid_amount, record?.total);
      },
    },
  ];

  const voucherColumns = [
    {
      title: "Voucher Date",
      dataIndex: "voucher_date",
      key: "voucher_date",
      width: "12%",
      render: (date) => (date ? moment(date).format("DD-MM-YYYY") : "-"),
    },
    {
      title: "Type",
      dataIndex: "voucher_type",
      key: "voucher_type",
      width: "8%",
    },
    {
      title: "Account",
      dataIndex: ["accounts", "account_name"],
      key: "account",
      width: "15%",
      render: (_, record) => record?.accounts?.account_name || "-",
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      width: "20%",
      render: (text) => text || "-",
    },
    {
      title: "Credit Total",
      dataIndex: "cr_total",
      key: "cr_total",
      width: "12%",
      render: (text) => Number(text || 0).toFixed(2),
    },
    {
      title: "Debit Total",
      dataIndex: "dr_total",
      key: "dr_total",
      width: "12%",
      render: (text) => Number(text || 0).toFixed(2),
    },
    {
      title: "Created Date",
      dataIndex: "createdat",
      key: "createdat",
      width: "12%",
      render: (date) => (date ? moment(date).format("DD-MM-YYYY") : "-"),
    },
    {
      title: "Updated Date",
      dataIndex: "updatedat",
      key: "updatedat",
      width: "12%",
      render: (date) => (date ? moment(date).format("DD-MM-YYYY") : "-"),
    },
  ];

  const voucherDetailColumns = [
    {
      title: "Account No",
      dataIndex: "account_no",
      key: "account_no",
      width: "12%",
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      width: "25%",
      render: (text) => text || "-",
    },
    {
      title: "Credit Amount",
      dataIndex: "amount_cr",
      key: "amount_cr",
      width: "15%",
      render: (text) => Number(text || 0).toFixed(2),
    },
    {
      title: "Debit Amount",
      dataIndex: "amount_dr",
      key: "amount_dr",
      width: "15%",
      render: (text) => Number(text || 0).toFixed(2),
    },
    {
      title: "Created Date",
      dataIndex: "createdat",
      key: "createdat",
      width: "15%",
      render: (date) => (date ? moment(date).format("DD-MM-YYYY") : "-"),
    },
    {
      title: "Updated Date",
      dataIndex: "updatedat",
      key: "updatedat",
      width: "15%",
      render: (date) => (date ? moment(date).format("DD-MM-YYYY") : "-"),
    },
  ];

  const ledgerColumns = [
    {
      title: "#",
      key: "index",
      width: 10,
      render: (_, __, index) => index + 1,
    },
    {
      title: "Date",
      dataIndex: "trans_date",
      key: "trans_date",
      width: "10%",
      render: (v) => (v ? moment(v).format("DD-MM-YYYY") : "-"),
    },
    {
      title: "Trans Type",
      dataIndex: "trans_type",
      key: "trans_type",
      width: "10%",
      render: (v) => v || "-",
    },
    {
      title: "Account",
      key: "account",
      width: "18%",
      render: (_, record) => {
        const accNo = record?.account_no || "-";
        const accName = record?.accounts?.account_name;
        return accName ? `${accNo} - ${accName}` : accNo;
      },
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      width: "10%",
      align: "right",
      render: (v) => (v != null ? Number(v).toFixed(2) : "-"),
    },
    {
      title: "Dr/Cr",
      dataIndex: "dr_or_cr",
      key: "dr_or_cr",
      width: "8%",
      align: "center",
      render: (v) => (
        <Tag color={v === "Dr" ? "green" : "red"}>{v || "-"}</Tag>
      ),
    },
    {
      title: "Note",
      dataIndex: "note",
      key: "note",
      width: "32%",
      ellipsis: true,
      render: (text) => text || "-",
    },
  ];

  if (loading) {
    return <LoadingBox />;
  }

  if (!purchaseData) {
    return (
      <div>
        <PageHeader breadScrum="Admin / Purchase / View" />
        <Card>
          <p>Purchase not found</p>
          <Button onClick={() => history.push("/auth/purchase")}>
            Back to Purchase List
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div>
      <PageHeader breadScrum="Admin / Purchase / View" />
      <Card
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => history.push("/auth/purchase")}
            >
              Back
            </Button>
            <span>Purchase Details</span>
          </div>
        }
      >
        <Descriptions bordered column={{ xxl: 2, xl: 2, lg: 2, md: 1, sm: 1 }}>
          <Descriptions.Item label="Supplier Name">
            {purchaseData?.supplierDetails?.account_name || "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Supplier Account No">
            {purchaseData?.supplierDetails?.account_no || "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Purchase Date">
            {purchaseData?.payment_date
              ? moment(purchaseData?.payment_date).format("DD-MM-YYYY")
              : "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Created At">
            {purchaseData?.created_at
              ? moment(purchaseData?.created_at).format("DD-MM-YYYY")
              : "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Updated At">
            {purchaseData?.updated_at
              ? moment(purchaseData?.updated_at).format("DD-MM-YYYY")
              : "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Description" span={2}>
            {purchaseData?.description || "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Total Amount">
            {Number(purchaseData?.total_amount || 0).toFixed(2)}
          </Descriptions.Item>
          <Descriptions.Item label="Total Discount">
            {Number(purchaseData?.total_discount || 0).toFixed(2)}
          </Descriptions.Item>
          <Descriptions.Item label="Paid Amount">
            {Number(purchaseData?.paid_amount || 0).toFixed(2)}
          </Descriptions.Item>
          <Descriptions.Item label="Outstanding">
            {Number(purchaseData?.outstanding || 0).toFixed(2)}
          </Descriptions.Item>
          <Descriptions.Item label="Payment Status">
            {getPaymentStatusTag(purchaseData?.payment_status)}
          </Descriptions.Item>
        </Descriptions>

        <div style={{ marginTop: 24 }}>
          <h3>Purchase Items</h3>
          <Table
            columns={detailColumns}
            dataSource={purchaseData?.purchaseDetails || []}
            pagination={false}
            rowKey="id"
            size="small"
            scroll={{ x: "max-content" }}
          />
        </div>

        {purchaseData?.ledgerTransactions?.length > 0 && (
          <div style={{ marginTop: 24 }}>
            <h3>Ledger Transactions</h3>
            <Table
              columns={ledgerColumns}
              dataSource={purchaseData?.ledgerTransactions || []}
              pagination={false}
              rowKey="id"
              size="small"
              scroll={{ x: "max-content" }}
            />
          </div>
        )}

        {purchaseData?.hasVoucher && purchaseData?.vouchers && (
          <div style={{ marginTop: 24 }}>
            <h3>Payment Vouchers</h3>
            <Table
              columns={voucherColumns}
              dataSource={purchaseData?.vouchers || []}
              pagination={false}
              rowKey="id"
              size="small"
              scroll={{ x: "max-content" }}
              expandable={{
                expandedRowRender: (record) => (
                  <div style={{ margin: "16px 0" }}>
                    <h4 style={{ marginBottom: 12 }}>Voucher Details</h4>
                    <Table
                      columns={voucherDetailColumns}
                      dataSource={record?.voucherDetails || []}
                      pagination={false}
                      rowKey="id"
                      size="small"
                      scroll={{ x: "max-content" }}
                    />
                  </div>
                ),
                rowExpandable: (record) =>
                  record?.voucherDetails && record?.voucherDetails?.length > 0,
              }}
            />
          </div>
        )}
      </Card>
    </div>
  );
};

export default PurchaseViewPage;

